// =====================================================================
// tpCustomer — shell: nav, topbar, hash router
// =====================================================================

const NAV_SECTIONS = [
  {
    // Shared records — rendered at the top of the sidebar without a heading.
    label: null,
    items: [
      { id: "sales/accounts",      label: "Accounts",      ico: "◫", desc: "Companies you sell to" },
      { id: "sales/contacts",      label: "Contacts",      ico: "◐", desc: "People you engage at accounts" },
      { id: "sales/library",       label: "Library",       ico: "▤", desc: "Sales collateral and shared documents" },
      { id: "sales/calendar",      label: "Calendar",      ico: "▤", desc: "Your calendar, tasks and follow-ups" },
      { id: "sales/activity",      label: "Activities",    ico: "◑", desc: "Upcoming follow-ups and history of logged interactions",
        aliases: ["sales/follow-ups", "sales/activities"] },
    ],
  },
  {
    label: "Sales",
    items: [
      { id: "sales/leads",         label: "Leads",         ico: "◔", tone: "green",  desc: "Inbound prospects to qualify" },
      { id: "sales/outreach",      label: "Outreach",      ico: "◈", tone: "green",  desc: "Sequences and marketing campaigns",
        // Legacy /sales/sequences and /sales/campaigns still resolve to
        // OutreachPage — keep the sidebar highlighted on those paths.
        aliases: ["sales/sequences", "sales/campaigns"] },
      { id: "sales/dashboard",     label: "Dashboard",     ico: "▦", tone: "yellow", desc: "Pipeline KPIs at a glance" },
      { id: "sales/opportunities", label: "Opportunities", ico: "◇", tone: "yellow", desc: "Active deals in your pipeline" },
      { id: "sales/quotes",        label: "Quotes",        ico: "◧", tone: "yellow", desc: "Pricing proposals tied to opportunities" },
      { id: "sales/forecast",      label: "Forecast",      ico: "◴", tone: "yellow", desc: "Projected revenue by period" },
      { id: "sales/churn",         label: "Churn",         ico: "◍", tone: "coral",  desc: "Retention health and at-risk accounts" },
      { id: "sales/pilots",        label: "Pilots",        ico: "◓", tone: "coral",  desc: "Pilot engagements — dashboard, list, checklist, log" },
      { id: "sales/partners",      label: "Partners",      ico: "⌬", tone: "blue",   desc: "Channel partners and resellers" },
      { id: "sales/referrals",     label: "Referrals",     ico: "⇄", tone: "blue",   desc: "Deals sourced through referrals" },
    ],
  },
  {
    label: "Research",
    items: [
      { id: "research/dashboard",   label: "Dashboard",    ico: "▩", tone: "yellow", desc: "Customer research overview" },
      { id: "research/insights",    label: "Insights",     ico: "◇", tone: "yellow", desc: "Themes synthesized from research" },
      { id: "research/surveys",     label: "Surveys",      ico: "◑", tone: "green",  desc: "Customer surveys and responses" },
      { id: "research/interviews",  label: "Interviews",   ico: "◐", tone: "green",  desc: "Recorded discovery interviews" },
      { id: "research/icp",         label: "ICPs",         ico: "◉", tone: "blue",   desc: "Ideal customer profile definitions" },
      { id: "research/champions",   label: "Champions",    ico: "★", tone: "blue",   desc: "Internal advocates inside accounts" },
      { id: "research/influencers", label: "Influencers",  ico: "◆", tone: "blue",   desc: "Decision influencers and interests" },
    ],
  },
];

function useHashRoute() {
  const [route, setRoute] = useState(() => window.location.hash.replace(/^#\/?/, "") || "sales/dashboard");
  useEffect(() => {
    const onHash = () => setRoute(window.location.hash.replace(/^#\/?/, "") || "sales/dashboard");
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);
  const navigate = useCallback((to) => { window.location.hash = "#/" + to; }, []);
  return [route, navigate];
}

// Self-service password change for the signed-in user (no MFA changes).
function ChangePasswordDialog({ onClose }) {
  const [cur, setCur]         = useState("");
  const [next, setNext]       = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState(null);
  const [done, setDone]       = useState(false);

  async function handleSave() {
    if (!cur)              { setError("Enter your current password."); return; }
    if (next.length < 8)   { setError("New password must be at least 8 characters."); return; }
    if (next !== confirm)  { setError("New passwords do not match."); return; }
    setSaving(true); setError(null);
    try {
      await tpAPI.request("/apicrm/auth/password", {
        method: "POST",
        body: { current_password: cur, new_password: next },
      });
      setDone(true);
      setTimeout(onClose, 1500);
    } catch (e) {
      setError(e.message || "Failed to change password.");
      setSaving(false);
    }
  }

  return (
    <Dialog title="Change password" onClose={onClose} footer={!done && <>
      <button className="btn btn-sm" onClick={onClose}>Cancel</button>
      <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>{saving ? "Saving…" : "Change password"}</button>
    </>}>
      {done ? <div className="quick-saved">✓ Password changed</div> : <>
        <div className="form-field"><label>Current password</label>
          <input type="password" autoComplete="current-password" value={cur} onChange={e => setCur(e.target.value)} autoFocus />
        </div>
        <div className="form-field"><label>New password (8+ characters)</label>
          <input type="password" autoComplete="new-password" value={next} onChange={e => setNext(e.target.value)} />
        </div>
        <div className="form-field"><label>Confirm new password</label>
          <input type="password" autoComplete="new-password" value={confirm} onChange={e => setConfirm(e.target.value)} />
        </div>
        {error && <div className="form-error">{error}</div>}
      </>}
    </Dialog>
  );
}

function NavSidebar({ route, navigate }) {
  const auth = useAuth();
  const [showPwd, setShowPwd] = useState(false);
  return (
    <nav className="nav">
      <div className="nav-brand">
        <img src="logo.png" alt="crmCustomer" style={{ height: 28, width: "auto", display: "block", flexShrink: 0 }} />
        <div className="nav-brand-name">crmCustomer</div>
      </div>

      {NAV_SECTIONS.map((section, sIdx) => (
        <React.Fragment key={section.label || `_unlabeled_${sIdx}`}>
          {section.label && <div className="nav-section">{section.label}</div>}
          {section.items.map((item) => {
            const active = route.startsWith(item.id)
              || (item.aliases || []).some(a => route.startsWith(a));
            return (
              <button key={item.id} type="button"
                   className={`nav-item${active ? " active" : ""}${item.tone ? " tone-" + item.tone : ""}`}
                   title={item.desc || item.label}
                   aria-current={active ? "page" : undefined}
                   onClick={() => navigate(item.id)}>
                <span className="nav-ico" aria-hidden="true">{item.ico}</span>
                <span className="nav-label">{item.label}</span>
              </button>
            );
          })}
        </React.Fragment>
      ))}

      <div className="nav-foot">
        <div className="nav-user">
          <div className="nav-user-avatar">{auth.user ? initials(auth.user.name) : "?"}</div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div className="nav-user-name">{auth.user?.name || "Sign in"}</div>
            <div className="nav-user-rolerow">
              <div className="nav-user-role">{auth.user?.role || "—"}</div>
              {auth.user && (
                <div className="nav-foot-actions">
                  <button type="button" className="nav-foot-icon" title="Change password"
                          aria-label="Change password" onClick={() => setShowPwd(true)}>🔑</button>
                  <button type="button" className="nav-foot-icon" title="Sign out"
                          aria-label="Sign out" onClick={auth.signOut}>🚪</button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {showPwd && <ChangePasswordDialog onClose={() => setShowPwd(false)} />}
    </nav>
  );
}

function SearchResultsPanel({ query, navigate, onClose }) {
  const q = query.trim().toLowerCase();
  if (!q) return null;

  function scoreItems(items, labelFn, subFn, routeFn, limit) {
    if (!Array.isArray(items)) return [];
    return items
      .filter(it => labelFn(it).toLowerCase().includes(q) || (subFn(it) || "").toLowerCase().includes(q))
      .slice(0, limit)
      .map(it => ({ label: labelFn(it), sub: subFn(it), route: routeFn(it) }));
  }

  const contacts = scoreItems(
    D.CONTACTS,
    c => [c.first, c.last].filter(Boolean).join(" "),
    c => c.title || c.account || "",
    c => "sales/contacts/" + c.id,
    4
  );
  const accounts = scoreItems(
    D.ACCOUNTS,
    a => a.name || "",
    a => a.segment || a.industry || "",
    a => "sales/accounts/" + a.id,
    4
  );
  const opps = scoreItems(
    D.OPPORTUNITIES,
    o => o.name || "",
    o => o.stage || "",
    o => "sales/opportunities/" + o.id,
    4
  );

  const total = contacts.length + accounts.length + opps.length;

  function ResultRow({ item }) {
    return (
      <button type="button" className="search-item"
              onClick={() => { navigate(item.route); onClose(); }}>
        <div>
          <div className="search-item-label">{item.label}</div>
          {item.sub && <div className="search-item-sub">{item.sub}</div>}
        </div>
      </button>
    );
  }

  return (
    <div className="search-results card">
      {total === 0 && <div className="search-empty">No results for "{query}"</div>}
      {contacts.length > 0 && (
        <>
          <div className="search-section">Contacts</div>
          {contacts.map((it, i) => <ResultRow key={i} item={it} />)}
        </>
      )}
      {accounts.length > 0 && (
        <>
          <div className="search-section">Accounts</div>
          {accounts.map((it, i) => <ResultRow key={i} item={it} />)}
        </>
      )}
      {opps.length > 0 && (
        <>
          <div className="search-section">Opportunities</div>
          {opps.map((it, i) => <ResultRow key={i} item={it} />)}
        </>
      )}
    </div>
  );
}

const SAMPLE_NOTIFICATIONS = [
  { id: 1, label: "Lumenly deal advanced to Proposal", sub: "2 hours ago" },
  { id: 2, label: "New lead assigned: Marcus Webb", sub: "Yesterday" },
  { id: 3, label: "Survey NPS-Q2 response received", sub: "2 days ago" },
  { id: 4, label: "Sequence \"SaaS Outbound\" step 3 due", sub: "3 days ago" },
];

function NotificationsPanel() {
  return (
    <div className="topbar-dropdown card">
      <div className="topbar-dropdown-head">Notifications</div>
      {SAMPLE_NOTIFICATIONS.map(n => (
        <div key={n.id} className="topbar-dropdown-item">
          <div className="topbar-dropdown-label">{n.label}</div>
          <div className="topbar-dropdown-sub">{n.sub}</div>
        </div>
      ))}
      <div className="topbar-dropdown-foot">All caught up</div>
    </div>
  );
}

const SHORTCUTS = [
  { keys: "/", desc: "Focus search" },
  { keys: "Esc", desc: "Close panel / dialog" },
  { keys: "G D", desc: "Go to Dashboard" },
  { keys: "G L", desc: "Go to Leads" },
  { keys: "G A", desc: "Go to Accounts" },
  { keys: "G C", desc: "Go to Contacts" },
  { keys: "G O", desc: "Go to Opportunities" },
];

const HELP_DOCS = [
  { href: "docs/howto.html",      label: "How-to guide",      sub: "Step-by-step workflows" },
  { href: "docs/flowguide.html",  label: "Flow guide",        sub: "Visual page-by-page flows" },
  { href: "docs/usermanual.html", label: "User manual",       sub: "Reference for every page and field" },
  { href: "docs/appguide.html",   label: "App guide",         sub: "Glossary and concepts" },
  { href: "docs/buying-committee-roles.html", label: "Buying committee", sub: "The seven roles in every B2B deal" },
];

function HelpPanel() {
  return (
    <div className="topbar-dropdown topbar-dropdown-sm card">
      <div className="topbar-dropdown-head">Guides</div>
      {HELP_DOCS.map(d => (
        <a key={d.href} className="topbar-dropdown-item"
           href={d.href} target="_blank" rel="noopener noreferrer"
           style={{ textDecoration: "none", color: "inherit", display: "block" }}>
          <div className="topbar-dropdown-label">{d.label}</div>
          <div className="topbar-dropdown-sub">{d.sub}</div>
        </a>
      ))}
    </div>
  );
}

function ShortcutsPanel() {
  return (
    <div className="topbar-dropdown topbar-dropdown-sm card">
      <div className="topbar-dropdown-head">Keyboard shortcuts</div>
      {SHORTCUTS.map(s => (
        <div key={s.keys} className="topbar-dropdown-item" style={{ justifyContent: "space-between", cursor: "default" }}>
          <span className="topbar-dropdown-label">{s.desc}</span>
          <kbd className="topbar-kbd">{s.keys}</kbd>
        </div>
      ))}
    </div>
  );
}

const NEW_ITEMS = [
  { label: "Account",     sub: "Add a company",          route: "sales/accounts" },
  { label: "Contact",     sub: "Add a person",           route: "sales/contacts" },
  { label: "Lead",        sub: "Qualify inbound",        route: "sales/leads" },
  { label: "Opportunity", sub: "Track a deal",           route: "sales/opportunities" },
];

function NewDropdown({ navigate, onClose }) {
  return (
    <div className="topbar-dropdown card">
      <div className="topbar-dropdown-head">Create new</div>
      {NEW_ITEMS.map(item => (
        <button key={item.label} type="button" className="topbar-dropdown-item"
             onClick={() => { navigate(item.route); onClose(); }}>
          <div>
            <div className="topbar-dropdown-label">{item.label}</div>
            <div className="topbar-dropdown-sub">{item.sub}</div>
          </div>
        </button>
      ))}
    </div>
  );
}

function Topbar({ route, navigate }) {
  const parts = route.split("/").filter(Boolean);
  const { tweaks, setTweak } = useContext(TweaksContext);
  const theme = tweaks.theme || "light";
  const [searchQuery, setSearchQuery] = useState("");
  const [searchFocus, setSearchFocus] = useState(false);
  const [panel, setPanel] = useState(null); // 'notifications' | 'help' | 'shortcuts' | 'new' | null
  const searchRef = useRef(null);
  const actionsRef = useRef(null);

  // "/" shortcut to focus search
  useEffect(() => {
    function onKey(e) {
      const tag = document.activeElement && document.activeElement.tagName;
      if (e.key === "/" && tag !== "INPUT" && tag !== "TEXTAREA") {
        e.preventDefault();
        searchRef.current && searchRef.current.focus();
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  // close panels on outside click
  useEffect(() => {
    if (!panel) return;
    function onDown(e) {
      if (actionsRef.current && !actionsRef.current.contains(e.target)) {
        setPanel(null);
      }
    }
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [panel]);

  function togglePanel(name) {
    setPanel(p => p === name ? null : name);
  }

  const showSearch = searchFocus && searchQuery.length > 0;

  return (
    <div className="topbar">
      <div className="topbar-crumbs">
        {parts.map((p, i) => (
          <React.Fragment key={i}>
            {i > 0 && <span className="subtle">/</span>}
            <span className={i === parts.length - 1 ? "crumb-current" : ""}>
              {p.replace(/-/g, " ").replace(/\b\w/g, c => c.toUpperCase())}
            </span>
          </React.Fragment>
        ))}
      </div>

      <div className="topbar-search">
        <span className="topbar-search-ico">⌕</span>
        <input
          ref={searchRef}
          placeholder="Search accounts, contacts, opportunities… (/)"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          onFocus={() => setSearchFocus(true)}
          onBlur={(e) => {
            // Keep the results open when focus moves into them, so a
            // mouse click or keyboard Enter on a result still registers.
            const wrap = e.currentTarget.closest(".topbar-search");
            if (wrap && e.relatedTarget && wrap.contains(e.relatedTarget)) return;
            setTimeout(() => setSearchFocus(false), 150);
          }}
          onKeyDown={e => { if (e.key === "Escape") { setSearchQuery(""); searchRef.current && searchRef.current.blur(); } }}
        />
        {showSearch && (
          <SearchResultsPanel
            query={searchQuery}
            navigate={navigate}
            onClose={() => { setSearchQuery(""); setSearchFocus(false); }}
          />
        )}
      </div>

      <div className="topbar-actions" ref={actionsRef}>
        <button
          className="topbar-icon-btn"
          title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
          aria-label="Toggle light / dark theme"
          onClick={() => setTweak("theme", theme === "dark" ? "light" : "dark")}
        >{theme === "dark" ? "☾" : "☀"}</button>
        <button
          className={`topbar-icon-btn${panel === "notifications" ? " active" : ""}`}
          title="Notifications"
          onClick={() => togglePanel("notifications")}
        >◔</button>
        <button
          className={`topbar-icon-btn${panel === "help" ? " active" : ""}`}
          title="Help &amp; guides"
          onClick={() => togglePanel("help")}
        >?</button>
        <button
          className={`topbar-icon-btn${panel === "shortcuts" ? " active" : ""}`}
          title="Keyboard shortcuts"
          aria-label="Keyboard shortcuts"
          onClick={() => togglePanel("shortcuts")}
        >⌨</button>
        <button
          className={`topbar-icon-btn${tweaks.__panelOpen ? " active" : ""}`}
          title="Tweaks"
          aria-label="Toggle tweaks panel"
          onClick={() => setTweak("__panelOpen", !tweaks.__panelOpen)}
        >✦</button>
        <button
          className={`topbar-icon-btn${route.startsWith("admin") ? " active" : ""}`}
          title="Settings"
          onClick={() => navigate("admin")}
        >⚙</button>
        <button
          className={`btn btn-primary btn-sm${panel === "new" ? " active" : ""}`}
          onClick={() => togglePanel("new")}
        >+ New</button>

        {panel === "notifications" && <NotificationsPanel />}
        {panel === "help" && <HelpPanel />}
        {panel === "shortcuts" && <ShortcutsPanel />}
        {panel === "new" && <NewDropdown navigate={navigate} onClose={() => setPanel(null)} />}
      </div>
    </div>
  );
}

function Shell({ children, route, navigate }) {
  return (
    <div className="app">
      <NavSidebar route={route} navigate={navigate} />
      <div className="main">
        <Topbar route={route} navigate={navigate} />
        <div className="content">{children}</div>
      </div>
    </div>
  );
}

Object.assign(window, { Shell, NavSidebar, Topbar, useHashRoute, NAV_SECTIONS });
